import React, {FC} from 'react';

import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {AsyncHandler, Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {computeAccountsSetupGuideCompletedTasks} from '../../../helpers/accountHelpers';
import {AccountsSetupGuideTasksEnum} from '../../../models/api/accountsSetupGuideModel';
import {SettingsPanelId} from '../../../store/panels/panelStateBase';
import {SetupGuide} from './common/setupGuide';
import {AddAccountCustomFieldTaskContainer} from './tasks/addAccountCustomFieldTaskContainer';
import {AddAccountRuleTaskContainer} from './tasks/addAccountRuleTaskContainer';
import {AddAccountTaskContainer} from './tasks/addAccountTaskContainer';
import {DiscoverFrontAccountsTask} from './tasks/discoverFrontAccountsTask';

/*
 * Props.
 */

interface AccountsSetupGuideProps {
  hasAccountsSetupGuide: boolean;
  shouldHideCompletedGuide?: boolean;
  accountsSetupGuideCompletedTasks: ReadonlyArray<AccountsSetupGuideTasksEnum>;
  fetchAccountsSetupGuideCompletedTasksAsync: AsyncHandler;
  onImportCSVClick?: Handler;
  onImportSalesforceClick?: Handler;
  // Dismissed state (from teammate preference).
  hasDismissedAccountsSetupGuide: boolean;
  updateHasDismissedAccountsSetupGuide: HandlerOf<boolean>;
  settingsPanelId?: SettingsPanelId;
}

/*
 * Component.
 */

/**
 * This intermediary component computes the overall guide progress and passes the
 * isComplete status to each task. The task components are themselves responsible
 * for setting up their own label and action.
 */
export const AccountsSetupGuide: FC<AccountsSetupGuideProps> = ({
  hasAccountsSetupGuide,
  shouldHideCompletedGuide,
  accountsSetupGuideCompletedTasks,
  fetchAccountsSetupGuideCompletedTasksAsync,
  onImportCSVClick,
  onImportSalesforceClick,
  hasDismissedAccountsSetupGuide,
  updateHasDismissedAccountsSetupGuide,
  settingsPanelId,
}) => {
  useInitialEffect(() => {
    fetchAccountsSetupGuideCompletedTasksAsync();
  });

  if (!hasAccountsSetupGuide || hasDismissedAccountsSetupGuide) {
    return null;
  }

  const {
    isDiscoverFrontAccountsComplete,
    isAddAccountCustomFieldComplete,
    isAddAccountComplete,
    isAddAccountRuleComplete,
  } = computeAccountsSetupGuideCompletedTasks(accountsSetupGuideCompletedTasks);

  // List of the isComplete status for each task.
  const taskIsCompletes = [
    isDiscoverFrontAccountsComplete,
    isAddAccountCustomFieldComplete,
    isAddAccountComplete,
    isAddAccountRuleComplete,
  ];

  const numCompletedTasks = taskIsCompletes.filter(Boolean).length;
  const progress = Math.floor((numCompletedTasks / taskIsCompletes.length) * 100);

  if (progress >= 100 && shouldHideCompletedGuide) {
    return null;
  }

  const onDismiss = () => updateHasDismissedAccountsSetupGuide(true);

  return (
    <SetupGuide progress={progress} onDismiss={onDismiss} settingsPanelId={settingsPanelId}>
      <DiscoverFrontAccountsTask isComplete={isDiscoverFrontAccountsComplete} />
      <AddAccountCustomFieldTaskContainer isComplete={isAddAccountCustomFieldComplete} />
      <AddAccountTaskContainer
        isComplete={isAddAccountComplete}
        onImportCSVClick={onImportCSVClick}
        onImportSalesforceClick={onImportSalesforceClick}
      />
      <AddAccountRuleTaskContainer isComplete={isAddAccountRuleComplete} />
    </SetupGuide>
  );
};
